import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { Settings2, Sparkles } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { InlineField } from "@/components/ui/inline-edit"
import { Select } from "@/components/ui/select"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { inlinePatch } from "@/lib/inline-save"
import { StatusBadge } from "@/components/studio/StatusBadge"
import {
  MEDIA_TYPES,
  MEDIA_TYPE_LABELS,
  formatCost,
  type MediaType,
  type StudioAsset,
  type StudioCapabilities,
  type StudioSettings,
  type StudioSuggestion,
} from "@/components/studio/types"
import type { PageProps } from "@/types/inertia"

type ArticleSummary = {
  id: number
  title_en: string
  status: string
  published_at: string | null
  asset_statuses: Partial<Record<MediaType, string>>
}

type PendingApproval = StudioAsset & { article_title: string; article_id: number }

type Props = PageProps & {
  articles: ArticleSummary[]
  pending_approvals: PendingApproval[]
  pending_suggestions: StudioSuggestion[]
  settings: StudioSettings
  ai_enabled: boolean
  media_enabled: boolean
  meta_enabled: boolean
  instagram_enabled: boolean
  ads_enabled: boolean
}

const MODE_LABELS: Record<string, string> = {
  on_demand: "On demand",
  on_publish: "When an article is published",
  nightly: "Nightly batch",
}

function CapabilityBanner({ capabilities }: { capabilities: StudioCapabilities }) {
  const missing: string[] = []
  if (!capabilities.ai_enabled) missing.push("ANTHROPIC_API_KEY (all AI drafting)")
  if (!capabilities.media_enabled) missing.push("FAL_KEY (image & video generation)")
  if (!capabilities.meta_enabled) missing.push("META_ACCESS_TOKEN + META_PAGE_ID (publishing)")
  if (!capabilities.ads_enabled) missing.push("META_AD_ACCOUNT_ID (ad uploads)")
  if (missing.length === 0) return null

  return (
    <div className="mt-4 rounded-lg border border-hairline bg-surface px-4 py-3">
      <p className="text-sm text-ink-muted">
        Some studio features are switched off until their keys are set: {missing.join(" · ")}.
      </p>
    </div>
  )
}

function SettingsDialog({ settings }: { settings: StudioSettings }) {
  const [open, setOpen] = React.useState(false)

  // The settings are one row the server rewrites whole, so a tick sends all
  // three groups as they stand with the one knob replaced.
  // Named rather than nested, so each knob commits on its own tick.
  const record: Record<string, string | boolean | number> = {
    ...Object.fromEntries(MEDIA_TYPES.map((t) => [ `mode:${t}`, settings.modes[t] ])),
    ...Object.fromEntries(MEDIA_TYPES.map((t) => [ `auto:${t}`, settings.auto_publish[t] ])),
    ad_batch_size: settings.ad_batch_size,
  }

  const fields = useInlineRecord({
    record,
    save: (key, value) => {
      const payload = {
        modes: { ...settings.modes },
        auto_publish: { ...settings.auto_publish },
        ad_batch_size: settings.ad_batch_size,
      }
      if (key.startsWith("mode:")) payload.modes[key.slice(5) as MediaType] = value as string
      else if (key.startsWith("auto:")) payload.auto_publish[key.slice(5) as MediaType] = value as boolean
      else payload.ad_batch_size = value as number
      return inlinePatch("/admin/studio/settings", { settings: payload })
    },
  })

  return (
    <>
      <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
        <Settings2 className="mr-1.5 h-4 w-4" />
        Automation settings
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Studio automation</DialogTitle>
            <DialogDescription>
              Choose when each media type is created and whether finished media posts to Meta
              automatically. Cartoon videos always wait for your approval before the paid
              generation, whatever the mode.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {MEDIA_TYPES.map((type) => (
              <div key={type} className="flex flex-col gap-2 rounded-md border border-hairline p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-ink-body">{MEDIA_TYPE_LABELS[type]}</p>
                  {type === "cartoon_video" && (
                    <p className="text-xs text-ink-muted">Always needs approval before generating.</p>
                  )}
                </div>
                <div className="flex flex-wrap items-start gap-4">
                  <InlineField state={fields.state(`mode:${type}`)}>
                    <Select
                      value={fields.value(`mode:${type}`) as string}
                      onChange={(e) => fields.set(`mode:${type}`, e.target.value)}
                      aria-label={`${MEDIA_TYPE_LABELS[type]} automation mode`}
                    >
                      {Object.entries(MODE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </InlineField>
                  <InlineField state={fields.state(`auto:${type}`)}>
                    <label className="flex items-center gap-2 font-normal text-ink-body">
                      <Checkbox
                        checked={fields.value(`auto:${type}`) as boolean}
                        onChange={(e) => fields.set(`auto:${type}`, e.target.checked)}
                      />
                      Auto-post when ready
                    </label>
                  </InlineField>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between rounded-md border border-hairline p-3">
              <div>
                <p className="text-sm text-ink-body">Ad variants per batch</p>
                <p className="text-xs text-ink-muted">5–10 distinct angles per generated batch.</p>
              </div>
              <InlineField state={fields.state("ad_batch_size")}>
                <Select
                  value={String(fields.value("ad_batch_size"))}
                  onChange={(e) => fields.set("ad_batch_size", Number(e.target.value))}
                  aria-label="Ad variants per batch"
                >
                  {[5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </InlineField>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function SuggestionCard({ suggestion }: { suggestion: StudioSuggestion }) {
  function act(action: "accept" | "dismiss") {
    router.patch(`/admin/studio/ad-suggestions/${suggestion.id}/${action}`, {}, { preserveScroll: true })
  }

  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="muted">Ads agent</Badge>
        {suggestion.article_title && (
          <span className="text-sm font-medium text-ink-display">{suggestion.article_title}</span>
        )}
      </div>
      <p className="mt-2 text-sm text-ink-body">{suggestion.summary}</p>
      {suggestion.rationale && <p className="mt-1 text-sm text-ink-muted">{suggestion.rationale}</p>}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {suggestion.proposed_angles.map((angle) => (
          <Badge key={angle.angle} tone="neutral" title={angle.brief}>
            {angle.angle.replace(/_/g, " ")}
          </Badge>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={() => act("accept")}>
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          Accept — generate new batch
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => act("dismiss")}>
          Dismiss
        </Button>
      </div>
    </li>
  )
}

export default function StudioIndex() {
  const { props } = usePage<Props>()
  const {
    articles,
    pending_approvals,
    pending_suggestions,
    settings,
    flash,
    ai_enabled,
    media_enabled,
    meta_enabled,
    instagram_enabled,
    ads_enabled,
  } = props
  const capabilities: StudioCapabilities = {
    ai_enabled,
    media_enabled,
    meta_enabled,
    instagram_enabled,
    ads_enabled,
  }

  return (
    <AdminShell>
      <Head title="Content Studio">
        <meta
          name="description"
          content="Turn articles into social posts, posters, ads, and short videos with AI — reviewed and published from one place."
        />
        <meta property="og:title" content="Content Studio" />
        <meta
          property="og:description"
          content="Turn articles into social posts, posters, ads, and short videos with AI — reviewed and published from one place."
        />
      </Head>
      <PageHeader
        title="Content Studio"
        description="Turn an article into social posts, posters, ad batches, and short videos — with approval gates on anything that costs money."
        actions={<SettingsDialog settings={settings} />}
      />

      {flash.notice && (
        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {flash.alert}
        </div>
      )}

      <CapabilityBanner capabilities={capabilities} />

      {pending_approvals.length > 0 && (
        <div className="mt-6 rounded-lg border border-hairline bg-surface">
          <div className="border-b border-hairline px-5 py-3">
            <h2>Waiting for your approval</h2>
            <p className="mt-0.5 text-sm text-ink-muted">
              Videos generate only after you approve the prompt and cost.
            </p>
          </div>
          <ul className="divide-y divide-hairline">
            {pending_approvals.map((asset) => (
              <li key={asset.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-ink-display">{asset.article_title}</p>
                  <p className="text-xs text-ink-muted">
                    {MEDIA_TYPE_LABELS[asset.media_type]} · {formatCost(asset.cost_estimate_cents)}
                  </p>
                </div>
                <Button type="button" size="sm" variant="secondary" asChild>
                  <Link href={`/admin/studio/articles/${asset.article_id}`} className="no-underline">
                    Review
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pending_suggestions.length > 0 && (
        <div className="mt-6 rounded-lg border border-hairline bg-surface">
          <div className="border-b border-hairline px-5 py-3">
            <h2>Ads agent suggestions</h2>
            <p className="mt-0.5 text-sm text-ink-muted">
              The weekly agent studies your ad performance and proposes the next batch. Nothing
              runs without your say-so — even accepted batches upload paused.
            </p>
          </div>
          <ul className="divide-y divide-hairline">
            {pending_suggestions.map((s) => (
              <SuggestionCard key={s.id} suggestion={s} />
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 rounded-lg border border-hairline bg-surface">
        <div className="border-b border-hairline px-5 py-3">
          <h2>Articles</h2>
          <p className="mt-0.5 text-sm text-ink-muted">
            Open an article to create and manage its derived media.
          </p>
        </div>
        {articles.length === 0 ? (
          <p className="px-5 py-6 text-center text-sm text-ink-muted">
            No articles yet — write one under Articles first.
          </p>
        ) : (
          <ul className="divide-y divide-hairline">
            {articles.map((article) => (
              <li key={article.id}>
                <Link
                  href={`/admin/studio/articles/${article.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 no-underline transition-colors hover:bg-page"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink-display">{article.title_en}</p>
                    <p className="text-xs text-ink-muted">
                      {article.status === "published" ? "Published" : "Draft"}
                      {article.published_at && ` · ${article.published_at}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {MEDIA_TYPES.map((type) =>
                      article.asset_statuses[type] ? (
                        <span key={type} title={MEDIA_TYPE_LABELS[type]}>
                          <StatusBadge status={article.asset_statuses[type]!} />
                        </span>
                      ) : null,
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminShell>
  )
}
